<template>
  <div class="top-bar-main">
    <ContainerLogo
      v-if="!mobileScreen || (mobileScreen && !showSearchInput)"
      class="top-bar-main__logo"
      :class="{
        'top-bar-main__logo__no-search-or-breadcrumb': mobileScreen
      }"
      :hoverText="hoverTextLogo"
      :logo-src="logo"
      role="button"
      tabindex="0"
      @click="reset"
      @keydown.enter="reset"
      @keydown.space="reset"
    />
    <BreadcrumbChevronTopBar v-if="!mobileScreen" class="top-bar-main__breadcrumbs" />
    <InputSearch
      v-if="!isIndoorviewShowing"
      class="top-bar-main__search"
      :show-search-input="showSearchInput"
      :mobile-screen="mobileScreen"
      :placeholderText="
        isIndoorviewShowing
          ? t('general__text__search-placeholder-IV')
          : realmId === 'akademiskahus'
          ? t('general__text__search-placeholder-AH')
          : t('general__text__search-placeholder')
      "
      :clearInput="clearInput"
      @toggleSearchInput="toggleSearchInput"
      @emit-search-string="searchStringValue"
    />
    <ButtonPrimary
      v-if="(!mobileScreen || (mobileScreen && !showSearchInput)) && !isIndoorviewShowing"
      class="search-button"
      :iconLeft="showSearchInput ? iconX : iconSearch"
      size="medium-icon"
      @onClicked="showSearchInput ? toggleSearchInput(false) : toggleSearchInput(true)"
    />
    <ButtonPrimary
      v-if="!mobileScreen"
      class="doodle-button"
      :iconLeft="iconBrushWhite"
      size="medium-icon"
      @onClicked="emits('openDoodle')"
    />
    <ButtonOutline
      v-if="!mobileScreen || (mobileScreen && !showSearchInput)"
      class="user-settings"
      :iconLeft="iconUser"
      size="medium-icon"
      @onClicked="toggleMenu"
    />
    <LayoutSearch
      v-if="showSearchInput"
      class="top-bar-main__search-menu"
      :isTyping="isTyping"
      :searchString="searchString"
      :mobileScreen="mobileScreen"
      @closeSearchOverlay="() => toggleSearchInput(false)"
      @clearSearch="() => (clearInput = true)"
    />
  </div>
  <MenuUserSettingsCard v-if="showMenu" @closeMenu="toggleMenu" />
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import BreadcrumbChevronTopBar from '@/design-system/molecules/BreadcrumbChevronTopBar.vue'
import ButtonOutline from '@/design-system/atoms/buttons/ButtonOutline.vue'
import ButtonPrimary from '@/design-system/atoms/buttons/ButtonPrimary.vue'
import ContainerLogo from '@/design-system/atoms/ContainerLogo.vue'
import MenuUserSettingsCard from '@/design-system/molecules/MenuUserSettingsCard.vue'
import { useStore } from 'vuex'
import { useGeneralStore } from '@/pinia'
import InputSearch from '@/design-system/molecules/InputSearch.vue'
import LayoutSearch from '@/design-system/organisms/LayoutSearch.vue'
import { setViewToMapview } from '@/utils/views'
import { useI18n } from 'vue-i18n'
import { getRealmId } from '@/utils/realmId'
import { zoomToEntities } from '@/composables/useMapbox'
import { useSelectionStore } from '@/pinia/selectionNew'
// import all images here instead of requiring them in the template
import logo from '@/design-system/assets/images/Logo-Digital-Buildings-Black.svg'
import iconX from '@/design-system/assets/icons/Icon-X.svg'
import iconSearch from '@/design-system/assets/icons/Icon-Search-WH.svg'
import iconBrushWhite from '@/design-system/assets/icons/Icon-Brush-01-White.svg'
import iconUser from '@/design-system/assets/icons/Icon-User.svg'

// emits
interface EMITS {
  (e: 'openDoodle')
  (e: 'toggleSearchOverlay') // Unused???
  (e: 'toggleSearchInput', active: boolean) // Unused???
}
const emits = defineEmits<EMITS>()

// reactive variables etc
const { t } = useI18n()
const piniaStore = useGeneralStore()
const clearInput = ref(false)
const hoverTextLogo = ref(
  `${t('general__text__show')} ${t('general__text__portfolio-entire').toLowerCase()} ${t(
    'general__text__in'
  ).toLowerCase()} ${t('general__view__mapview').toLowerCase()}`
)
const isTyping = ref(false)
const realmId = getRealmId()
const searchString = ref('')
const showMenu = ref(false)
const showSearchInput = ref(false)
const store = useStore()

// computed
const allComplexes = computed(() => store.getters.allComplexes)
const isIndoorviewShowing = computed(() => store.getters.isIndoorviewShowing)
const mobileScreen = computed<boolean>(() => store.state.mobileScreen)
const { showOverlayModalPinia } = storeToRefs(piniaStore)
const selection = useSelectionStore()

async function reset() {
  if (showOverlayModalPinia.value) piniaStore.setShowOverlayModalPinia(false)
  await store.dispatch('RESET_ALL_SELECTIONS')
  store.commit('SET_CLICKED_ITEM_TYPE', { type: 'region' })
  setViewToMapview()
  selection.clear()
  zoomToEntities(allComplexes.value.map((complex) => complex.id))
}

function searchStringValue(searchValue: string) {
  if (searchValue.length >= 1) {
    isTyping.value = true
    clearInput.value = false
  } else {
    isTyping.value = false
  }
  searchString.value = searchValue
}

function toggleMenu() {
  showMenu.value = !showMenu.value
}

function toggleSearchInput(active: boolean) {
  // If the tickets overlay modal is open while search is activated, the user could end up with a half empty-modal after selecting a new object.
  // Perhaps the piniaStore.setShowOverlayModalPinia(false) function below should be moved to category selection and search selection instead,
  // but we currently have category and search bugs which need to be fixed first. Placing the function here is a temporary fix to prevent errors.
  if (showOverlayModalPinia.value) piniaStore.setShowOverlayModalPinia(false) // Move when bugs have been fixed?
  showSearchInput.value = active
  clearInput.value = !active
  if (!active) searchStringValue('')
  emits('toggleSearchOverlay')
}
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
.top-bar-main {
  align-items: center;
  background-color: variables.$color-pure-white;
  box-shadow: variables.$box-shadow-small;
  column-gap: variables.$gap-xlarge;
  display: grid;
  grid-template-columns:
    minmax(variables.$logo-width, auto) repeat(12, 1fr)
    minmax(calc(#{variables.$button-icon-medium} * 3 + 8px + #{variables.$margin-xxlarge}), auto);
  height: variables.$layout-top-bar-height;
  position: fixed;
  width: 100%;
  z-index: variables.$z-index-top-bar;

  @media screen and (max-width: 1024px) {
    grid-template-columns:
      minmax(variables.$margin-medium, auto) repeat(12, 1fr)
      minmax(variables.$margin-medium, auto);
    column-gap: variables.$gap-large;
  }

  &__logo {
    cursor: pointer;
    grid-column: 1;
    padding-left: variables.$padding-xlarge;

    @media screen and (max-width: 1024px) {
      margin-right: variables.$margin-medium;
    }

    &__no-click {
      cursor: default;
    }
  }
  &__breadcrumbs {
    grid-column: 2 / 9;
    grid-row: 1;
  }

  .search__input {
    grid-column: 9 / 15;
    grid-row: 1;
    height: inherit;
    justify-self: right;
    padding-right: calc(#{variables.$padding-main-body} * 7);
    transition: width 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    width: 80%;
    &-expanded {
      grid-column-start: 2;
      width: 100%;
    }
    @media screen and (max-width: 1024px) {
      grid-column: 12 / 14;
      padding: 0;
      transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
      &-expanded {
        grid-column: 1 / -1;
        padding-right: variables.$padding-xlarge;
      }
    }
  }
  &__search-menu {
    column-gap: inherit;
    display: grid;
    grid-column: 1 / -1;
    grid-template-columns: inherit;
    position: relative;
    top: 0;
  }
}
.user-settings {
  grid-column: 14;
  grid-row: 1;
  justify-self: end;
  margin-right: variables.$margin-xlarge;
  position: relative;
}
.doodle-button {
  grid-column: 14;
  grid-row: 1;
  margin-left: auto;
  margin-right: calc(#{variables.$margin-xlarge} + 8px + 40px);
  @media (pointer: coarse) {
    display: none; // hide also for large touch devices
  }
}
.search-button {
  grid-row: 1;
  grid-column: 14;
  justify-self: right;
  margin-right: calc(#{variables.$padding-main-body} * 5);

  @media screen and (max-width: 1024px) {
    margin-block: variables.$margin-large;
    margin-right: 0;
    grid-column: 13;
  }
}
</style>
